import type { Edit } from '@w/crdt'
import { indexViewBlocks, pageDocument, type PageBlock, type PageValue } from './doc-blocks'

/** A page title and its visible metadata field share the same authored value. */
export function preparePageMetadata(tx: Edit): void {
  const delta = tx.delta()
  const before = pageDocument({ toJSON: () => delta.before })
  const after = pageDocument({ toJSON: () => delta.after })
  const metadata = firstPageMeta(delta.after)
  if (!metadata) return
  const same = blockAt(delta.before, metadata.path)
  const previous = same?.id === metadata.block.id ? same : indexViewBlocks(delta.before).get(metadata.block.id)?.block
  const field = metadata.block.title
  const fieldChanged = previous?.title !== field && typeof field === 'string' && (previous !== undefined || field !== '')
  const title = fieldChanged ? field : after.title ?? (typeof field === 'string' ? field : '')
  const chosen = after.title === before.title ? title : after.title ?? ''
  if (after.title !== chosen) tx.set('', 'title', chosen)
  if (field !== chosen) tx.set(metadata.path, 'title', chosen)
}

/** Frozen view objects searched whole without a pageMeta. Views share every untouched object, so a later view's search
 * walks only its changed path (crdt view patches). */
const withoutPageMeta = new WeakSet<object>()

/** The first pageMeta of `view` in document (pre-)order with its path, as `indexViewBlocks` orders and names blocks:
 * the header may sit in any list or JSON field (doc-bindings-for-page.test.ts). */
function firstPageMeta(view: unknown): { block: PageBlock; path: string } | undefined {
  const search = (value: PageValue, path: string): { block: PageBlock; path: string } | undefined => {
    if (!value || typeof value !== 'object' || withoutPageMeta.has(value)) return undefined
    let found: { block: PageBlock; path: string } | undefined
    // Plain loops: this walk visits every object of a page once (no entries arrays), its paths only on a match.
    if (isList(value)) {
      for (let index = 0; index < value.length; index++) {
        const item = value[index]
        if (!item || typeof item !== 'object') continue
        const block = isBlock(item)
        if (block && item.type === 'pageMeta') return { block: item, path: `${path}.${item.id}` }
        found = search(item, block ? `${path}.${item.id}` : `${path}.[${index}]`)
        if (found) return found
      }
    } else {
      const record = value
      for (const key in record) {
        const item = record[key]
        if (!item || typeof item !== 'object') continue
        found = search(item, path ? `${path}.${key}` : key)
        if (found) return found
      }
    }
    if (Object.isFrozen(value)) withoutPageMeta.add(value)
    return undefined
  }
  return search(view as PageValue, '')
}

/** The block at an `indexViewBlocks` path of `view`, if there is one. */
function blockAt(view: unknown, path: string): PageBlock | undefined {
  let current = view as PageValue | undefined
  for (const part of path.split('.')) {
    if (current !== undefined && isList(current)) current = /^\[\d+\]$/u.test(part) ? current[Number(part.slice(1, -1))]
      : current.find(item => isBlock(item) && item.id === part)
    else if (current && typeof current === 'object') current = current[part]
    else return undefined
  }
  return isBlock(current) ? current : undefined
}

function isList(value: PageValue): value is readonly PageValue[] {
  return Array.isArray(value)
}

function isBlock(value: unknown): value is PageBlock {
  return value !== null && typeof value === 'object' && !Array.isArray(value) &&
    typeof (value as { id?: unknown }).id === 'string' && typeof (value as { type?: unknown }).type === 'string'
}
